import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import {
  DEFAULT_SEND_WINDOWS,
  SendWindow,
  hhmmOf,
  minutesOf,
  spParts,
  spToday,
  spWallTimeToDate,
} from 'src/app/models/SendWindow';

/**
 * Data + hora com a janela de envio bloqueada (M8 / CU-17tm466nzee,
 * m8-comum.md). A hora é livre, de minuto em minuto (CU-17tm466v2e4 — antes
 * era uma grade de 30 em 30 min): data passada bloqueada, hora fora de
 * `[window.start, window.stop)` ou já passada (hoje) fica marcada como
 * bloqueada e NÃO chega ao formulário (emite `null`, como o `<select>` antigo,
 * que não deixava escolher a opção desabilitada). O valor já preenchido via
 * `writeValue` NUNCA é apagado, mesmo bloqueado — quem decide o que fazer com
 * isso é a UI (mostra bloqueado), não o CVA. "Já passou" só vale depois que o
 * usuário mexe no campo: editar uma ligação em andamento abre com início no
 * passado, de propósito, e não pode nascer acusando erro. Tudo em horário de Brasília,
 * independente do fuso do navegador.
 *
 * Também é validador (`NG_VALIDATORS`, erro `sendWindow`): hora bloqueada DIGITADA
 * deixa o controle inválido, não só vazio. Sem isso, um campo opcional (a data final
 * do editar de Ligação) virava `null` em silêncio e o Salvar seguia liberado
 * (retorno do review, 29/10). Valor gravado vindo do `writeValue` não invalida: quem
 * barra esse caso é o pai, no submit, com a mensagem dele. Data ou hora apagada (ou hora
 * digitada pela metade, que o `<input type="time">` entrega como vazia) depois de o
 * usuário mexer também invalida, com aviso: sem isso a data final gravada do editar de
 * Ligação sumia em silêncio no PUT (retorno do review r2, 29/10).
 *
 * Valor do CVA é `Date | null` (não string): é o mesmo tipo que
 * `broadcast.component.ts` já usa no `FormControl('scheduled_date')` e que o
 * `Date.prototype.toJSON` global (`app.module.ts`) serializa como hora de
 * parede de São Paulo — `spWallTimeToDate` monta o instante absoluto certo
 * para isso bater em qualquer fuso de navegador.
 */
@Component({
  selector: 'app-window-datetime-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './window-datetime-picker.component.html',
  styleUrls: ['./window-datetime-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WindowDatetimePickerComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WindowDatetimePickerComponent),
      multi: true
    }
  ]
})
export class WindowDatetimePickerComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() window: SendWindow = DEFAULT_SEND_WINDOWS.sms;
  @Input() testId?: string;

  disabled = false;
  datePart = '';
  timePart = '';
  /** O usuário mexeu na data ou na hora desde o último `writeValue`. */
  private edited = false;
  /** O último `writeValue` trouxe data e hora (valor gravado). */
  private hadValue = false;
  private dateTouched = false;
  private timeTouched = false;

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  get todaySp(): string {
    return spToday();
  }

  get minDate(): string {
    return this.todaySp;
  }

  /** Último minuto liberado ('HH:mm') — `max` do `<input type="time">` (fim da janela é exclusivo). */
  get maxTime(): string {
    return hhmmOf(minutesOf(this.window.stop) - 1);
  }

  /** Mensagem do horário bloqueado (fora da janela ou já passado hoje), ou `null`. */
  get timeError(): string | null {
    if (!this.timePart) {
      return null;
    }
    const minute = minutesOf(this.timePart);
    if (minute < minutesOf(this.window.start) || minute >= minutesOf(this.window.stop)) {
      return `${this.timePart} está bloqueado: escolha entre ${this.window.start} e ${this.maxTime}.`;
    }
    if (this.edited && this.datePart && this.datePart === this.todaySp) {
      const { hour, minute: nowMinute } = spParts();
      if (minute <= hour * 60 + nowMinute) {
        return `${this.timePart} já passou: escolha um horário futuro.`;
      }
    }
    return null;
  }

  /**
   * Data ou hora que ficou vazia depois de o usuário mexer: a que ele apagou (ou digitou
   * pela metade), ou qualquer uma das duas se o campo já tinha valor gravado. Só escolher
   * a data de um campo novo ainda não acusa a hora que falta.
   */
  get incompleteError(): string | null {
    if (!this.edited) {
      return null;
    }
    const missingDate = !this.datePart && (this.dateTouched || this.hadValue);
    const missingTime = !this.timePart && (this.timeTouched || this.hadValue);
    if (!missingDate && !missingTime) {
      return null;
    }
    if (missingDate && missingTime) {
      return 'Preencha a data e a hora.';
    }
    return missingDate ? 'Preencha a data.' : 'Preencha a hora completa (HH:mm).';
  }

  /** Erro mostrado embaixo do campo e devolvido pelo validador. */
  get fieldError(): string | null {
    return this.timeError || this.incompleteError;
  }

  /**
   * A janela pode chegar (`GET /send-windows`) ou mudar depois de o usuário já ter
   * digitado: a hora que era válida pode ter ficado bloqueada (ou o contrário). Reemite
   * o valor e reavalia o validador para o formulário não ficar com o resultado velho.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['window'] && !changes['window'].firstChange && this.edited) {
      this.emit();
      this.onValidatorChange();
    }
  }

  // --- ControlValueAccessor ---------------------------------------------

  writeValue(value: Date | string | number | null): void {
    // `broadcastForm.patchValue(broadcast)` grava o que a API devolver — hoje
    // uma string ISO (`scheduled_date`), não um `Date` — e `Intl.DateTimeFormat`
    // não aceita string (`ToNumber` dá NaN e lança RangeError). Normaliza aqui
    // em vez de confiar no tipo declarado do CVA, que é só compile-time.
    this.edited = false;
    this.dateTouched = false;
    this.timeTouched = false;
    const date = value instanceof Date ? value : value ? new Date(value) : null;

    this.hadValue = !!date && !isNaN(date.getTime());
    if (!this.hadValue) {
      this.datePart = '';
      this.timePart = '';
      return;
    }

    const { year, month, day, hour, minute } = spParts(date!);
    this.datePart = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    this.timePart = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Validator -----------------------------------------------------------

  validate(_control: AbstractControl): ValidationErrors | null {
    const error = this.edited ? this.fieldError : null;
    return error ? { sendWindow: error } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // --- Interação -----------------------------------------------------------

  onDateChange(date: string): void {
    this.edited = true;
    this.dateTouched = true;
    this.datePart = date || '';
    this.emit();
  }

  onTimeChange(time: string): void {
    this.edited = true;
    this.timeTouched = true;
    this.timePart = time || '';
    this.emit();
  }

  private emit(): void {
    this.onTouched();
    const value = this.datePart && this.timePart && !this.timeError
      ? spWallTimeToDate(this.datePart, this.timePart)
      : null;
    this.onChange(value);
  }
}
